import { useCallback, useMemo, useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Tooltip } from "@mui/material";
import { AddIcon, CloseIcon } from "../../assets/composer-editor-icons";
import DraftConfirmationPopup from "../popups/admin/DraftConfirmationPopup";
import { patchWithEtag } from "../../tools/conditionalApi";
import { formatSaveError, LONG_REQUEST_TIMEOUT_MS } from "../../utils/fileUploadHelpers";
import useLongAction, { actionFailure } from "../shared/long-action/useLongAction";
import ActionPanel from "../shared/long-action/ActionPanel";
import {
  COMPOSER_AVAILABLE_LANGUAGES,
  formatLanguageName,
  getCourseSupportedLanguages,
  normalizeLanguageKey,
} from "../../utils/composerLanguages";

/**
 * Sidebar-launched dialog for managing a program's supported languages:
 * add a language, remove one (with an explicit confirmation), and pick the
 * default (authoring-order) language. Saves straight to the course so it
 * works from any composer page.
 */
const ManageLanguagesDialog = ({
  open,
  onClose,
  course,
  /** Called with the PATCH response after a successful save (refresh the course tree). */
  onSaved,
  showError,
}) => {
  const { enabled } = useLongAction();
  const [answer, setAnswer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const supportedLanguages = useMemo(
    () => getCourseSupportedLanguages(course),
    [course]
  );

  const remainingLanguages = useMemo(
    () =>
      COMPOSER_AVAILABLE_LANGUAGES.filter(
        ({ value }) => !supportedLanguages.includes(value)
      ),
    [supportedLanguages]
  );

  const saveLanguages = useCallback(
    async (nextLanguages) => {
      if (!course?.id || saving) return;
      setSaving(true);
      try {
        const { data } = await patchWithEtag(`/backoffice/v1/courses/${course.id}/`, {
          id: course.id,
          supported_languages: nextLanguages,
        }, enabled ? { timeout: LONG_REQUEST_TIMEOUT_MS } : {});
        await onSaved?.(data);
        if (enabled) setAnswer({ message: "Program languages saved." });
      } catch (err) {
        console.error("Failed to update program languages:", err);
        if (enabled) setAnswer(actionFailure(err, (error) => formatSaveError(error, "program languages")));
        else showError?.(formatSaveError(err, "program languages"));
      } finally {
        setSaving(false);
      }
    },
    [course?.id, enabled, onSaved, saving, showError]
  );

  const handleAddLanguage = useCallback(
    (lang) => {
      const normalized = normalizeLanguageKey(lang);
      if (!normalized || supportedLanguages.includes(normalized)) return;
      saveLanguages([...supportedLanguages, normalized]);
    },
    [saveLanguages, supportedLanguages]
  );

  const handleSetDefault = useCallback(
    (lang) => {
      const normalized = normalizeLanguageKey(lang);
      if (!normalized || supportedLanguages[0] === normalized) return;
      if (!supportedLanguages.includes(normalized)) return;
      saveLanguages([
        normalized,
        ...supportedLanguages.filter((l) => l !== normalized),
      ]);
    },
    [saveLanguages, supportedLanguages]
  );

  const confirmRemoveLanguage = useCallback(() => {
    const normalized = normalizeLanguageKey(pendingRemoval);
    setPendingRemoval(null);
    if (!normalized || !supportedLanguages.includes(normalized)) return;
    if (supportedLanguages.length <= 1) {
      showError?.("At least one supported language is required.");
      return;
    }
    saveLanguages(supportedLanguages.filter((l) => l !== normalized));
  }, [pendingRemoval, saveLanguages, showError, supportedLanguages]);

  return (
    <>
      {/* Hidden while the removal confirmation is up: MUI would aria-hide and focus-trap
          everything outside its portal, making the confirm popup inaccessible. */}
      <Dialog
        open={open && !pendingRemoval}
        onClose={(...args) => { if (!(enabled && (saving || answer))) onClose?.(...args); }}
        disableEscapeKeyDown={enabled && Boolean(saving || answer)}
        sx={enabled ? { zIndex: 13000 } : undefined}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "pd-manage-languages-dialog" }}
      >
        {enabled && (saving || answer) ? <ActionPanel working={saving} label={saving ? "Saving program languages…" : "Program languages"}
          {...answer} onClose={() => setAnswer(null)} /> : <>
        <DialogTitle className="pd-manage-languages-title">
          Program languages
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            className="pd-manage-languages-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className="pd-manage-languages-content">
          <p className="pd-manage-languages-hint">
            Learners can only open this program in one of its supported languages.
          </p>
          <ul className="pd-manage-languages-list" aria-label="Supported languages">
            {supportedLanguages.map((lang, index) => (
              <li key={lang} className="pd-manage-languages-row">
                <span className="pd-manage-languages-name">
                  {formatLanguageName(lang)}
                  {index === 0 ? (
                    <span className="pd-manage-languages-default-badge">Default</span>
                  ) : null}
                </span>
                <span className="pd-manage-languages-row-actions">
                  {index !== 0 ? (
                    <button
                      type="button"
                      className="pd-manage-languages-make-default"
                      onClick={() => handleSetDefault(lang)}
                      disabled={saving}
                    >
                      Make default
                    </button>
                  ) : null}
                  {supportedLanguages.length > 1 ? (
                    <Tooltip title={`Remove ${formatLanguageName(lang)}`} arrow placement="top">
                      <button
                        type="button"
                        className="pd-manage-languages-remove"
                        onClick={() => setPendingRemoval(lang)}
                        disabled={saving}
                        aria-label={`Remove ${formatLanguageName(lang)}`}
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    </Tooltip>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          {remainingLanguages.length > 0 ? (
            <div className="pd-manage-languages-add-row">
              {remainingLanguages.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  className="pd-manage-languages-add"
                  onClick={() => handleAddLanguage(lang.value)}
                  disabled={saving}
                >
                  <AddIcon fontSize="small" />
                  Add {lang.label}
                </button>
              ))}
            </div>
          ) : (
            <p className="pd-manage-languages-hint pd-manage-languages-all-added">
              All available languages have been added.
            </p>
          )}
        </DialogContent>
        </>}
      </Dialog>

      <DraftConfirmationPopup
        layerClassName="pd-manage-languages-confirm-layer"
        open={Boolean(pendingRemoval)}
        close={() => setPendingRemoval(null)}
        confirm={confirmRemoveLanguage}
        title={`Remove ${formatLanguageName(pendingRemoval || "")}?`}
        description={`${formatLanguageName(pendingRemoval || "")} will be removed from the program. Content in this language will no longer be shown to learners or in the editor.`}
        cancelVariant="neutral"
        confirmVariant="danger"
        confirmText="Remove language"
      />
    </>
  );
};

export default ManageLanguagesDialog;
